import React, { useState } from 'react';
import { useApi } from '../../hooks/useApi';

// Sprint 21.2. Outcome taxonomy per BK: this isn't a single terminal status —
// 'no_reply' reopens the reminder with a follow-up date, same as T2M's
// follow-up pattern, rather than closing it out. 'cancelled' hands off to the
// existing voluntary_cancellation retention flow server-side (see
// logRenewalReminderOutcome in queries.js) — "an entirely new flow," per BK.

const OUTCOMES = [
  { value: 'no_reply', label: 'No Reply / No Answer', color: 'var(--orange)' },
  { value: 'renewed_annual', label: 'Renewed — Annual', color: 'var(--green)' },
  { value: 'renewed_monthly', label: 'Renewed — Changed to Monthly', color: 'var(--green)' },
  { value: 'cancelled', label: 'Cancelled', color: 'var(--red)' },
];

// Promotion ending (11/3/26): the member signs up for a regular plan or lets the promotion end.
// 'not_renewing' does NOT open a cancellation case (nothing is being cancelled).
const PROMO_OUTCOMES = [
  { value: 'no_reply', label: 'No Reply / No Answer', color: 'var(--orange)' },
  { value: 'renewed_monthly', label: 'Signed up — Monthly', color: 'var(--green)' },
  { value: 'renewed_annual', label: 'Signed up — Annual', color: 'var(--green)' },
  { value: 'not_renewing', label: 'Not renewing (letting it end)', color: 'var(--red)' },
];

const CONTACT_METHODS = [
  { value: 'call', label: '📞 Call' },
  { value: 'text', label: '💬 Text' },
  { value: 'email', label: '📧 Email' },
];

const QUICK_PICKS = [
  { label: 'Tomorrow', days: 1 },
  { label: 'In 2 days', days: 2 },
  { label: 'In 3 days', days: 3 },
];

function addDays(n) {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}

export default function RenewalReminderOutcomeModal({ reminder, onClose, onSaved }) {
  const { post } = useApi();
  const [contactMethod, setContactMethod] = useState('');
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const needsFollowUp = outcome === 'no_reply';
  const isPromo = reminder.touch_stage === '14_day';
  const outcomes = isPromo ? PROMO_OUTCOMES : OUTCOMES;

  async function handleSave() {
    if (!outcome) { setError('Pick an outcome.'); return; }
    if (needsFollowUp && !followUpDate) { setError('Pick a follow-up date.'); return; }
    setSaving(true);
    setError(null);
    try {
      await post(`/renewal-reminders/${reminder.id}/outcome`, {
        outcome,
        contactMethod: contactMethod || null,
        notes: notes || null,
        followUpDate: needsFollowUp ? followUpDate : null,
      });
      onSaved(outcome);
    } catch (err) {
      setError(err.message || 'Failed to save.');
      setSaving(false);
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }}>
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
        padding: '1.5rem', width: 420, maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto',
      }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
          Log Outcome — {reminder.member_name || reminder.email}
        </div>
        <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginBottom: 16 }}>
          {isPromo ? 'Promotion ending · does not renew on its own'
            : `${reminder.touch_stage === '45_day' ? '45-Day' : '7-Day'} touch · ${reminder.plan_length === 'annual' ? 'Annual' : 'Extended Period'}`}
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>Contact Method</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {CONTACT_METHODS.map((m) => (
              <button
                key={m.value}
                className="btn"
                onClick={() => setContactMethod(m.value)}
                style={{
                  fontSize: 12, border: `1px solid ${contactMethod === m.value ? 'var(--yellow)' : 'var(--border)'}`,
                  background: contactMethod === m.value ? 'var(--yellow-dim)' : 'transparent',
                  color: contactMethod === m.value ? 'var(--yellow)' : 'var(--text-secondary)',
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>Outcome</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {outcomes.map((o) => (
              <button
                key={o.value}
                className="btn"
                onClick={() => setOutcome(o.value)}
                style={{
                  justifyContent: 'flex-start',
                  border: `1px solid ${outcome === o.value ? o.color : 'var(--border)'}`,
                  background: outcome === o.value ? `${o.color}22` : 'transparent',
                  color: outcome === o.value ? o.color : 'var(--text-secondary)',
                }}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {isPromo && (outcome === 'renewed_monthly' || outcome === 'renewed_annual') && (
          <div style={{
            background: 'var(--yellow-dim)', color: 'var(--yellow)', fontSize: 12,
            padding: '8px 10px', borderRadius: 6, marginBottom: 14,
          }}>
            ⚠ Set up the new plan in PbP to start the day the promotion ends, with a card on file.
          </div>
        )}

        {!isPromo && outcome === 'renewed_annual' && (
          <div style={{
            background: 'var(--yellow-dim)', color: 'var(--yellow)', fontSize: 12,
            padding: '8px 10px', borderRadius: 6, marginBottom: 14,
          }}>
            ⚠ Don't forget: set the renewal date in PbP and confirm payment on the card on file.
          </div>
        )}

        {outcome === 'cancelled' && (
          <div style={{
            background: 'var(--red-dim)', color: 'var(--red)', fontSize: 12,
            padding: '8px 10px', borderRadius: 6, marginBottom: 14,
          }}>
            This will open a voluntary cancellation case in Retention for this member.
          </div>
        )}

        {needsFollowUp && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>Follow Up</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
              {QUICK_PICKS.map((q) => (
                <button
                  key={q.days}
                  className="btn btn-ghost btn-sm"
                  onClick={() => setFollowUpDate(addDays(q.days))}
                  style={{ borderColor: followUpDate === addDays(q.days) ? 'var(--yellow)' : undefined }}
                >
                  {q.label}
                </button>
              ))}
            </div>
            <input
              className="field-input"
              type="date"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              style={{ width: '100%' }}
            />
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6, color: 'var(--text-secondary)' }}>Notes</div>
          <textarea
            className="field-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            style={{ width: '100%', resize: 'vertical' }}
          />
        </div>

        {error && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
